export function convertLocalDateToUTCDate(localDateString: string|Date) { 
  let localDate: Date;

  if(localDateString instanceof Date) {
    localDateString = toISODateString(localDateString)
  }

  try {
    localDate = new Date(asISODateString(localDateString));
  } catch {
    localDate = new Date(); 
  }

  // Force the local clock numbers into a UTC timestamp
  const forcedUtcDate = new Date(Date.UTC(
    localDate.getFullYear(),
    localDate.getMonth(),
    localDate.getDate(),
    localDate.getHours(),
    localDate.getMinutes(),
    localDate.getSeconds()
  ));

  return forcedUtcDate; // UTC Date (timestamp is now in UTC)
  //return forcedUtcDate.toISOString();
}

export function hoursToSeconds(hours: number): number {
  return hours * 3600;
}

export function minutesToSeconds(minutes: number): number {
  return minutes * 60;
}

export function secondsToHours(seconds: number): number {
  return seconds / 3600;
}

export function secondsToMilliseconds(seconds: number): number {
  return seconds * 1000;
}

export function secondsToMinutes(seconds: number): number {
  return seconds / 60;
}

interface DateTimeFormatOptions extends Intl.DateTimeFormatOptions {
  locale?: string | undefined;
  preset?: "iso" | "isoUTC" | "short" | "medium" | "long";
}

export function formatDateTime(dateString: string, options: DateTimeFormatOptions = {}) {
  const utcDate = new Date(dateString);

  // Destructure to separate non-format keys from actual Intl options
  const { preset, locale: optionLocale, ...dateTimeOptions } = options;

  // Locale fallback
  const locale = optionLocale || undefined;

  // Helper for padding numbers
  const pad = (num: number) => String(num).padStart(2, "0");

  const presets: Record<"short" | "medium" | "long", Intl.DateTimeFormatOptions> = {
    short: {
      year: "2-digit",
      month: "numeric",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    },
    medium: {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit"
    },
    long: {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit"
    }
  };

  // If ISO preset is chosen, return custom formatted string
  if(preset === "iso") {
    const pad = (num: number) => String(num).padStart(2, "0");
    const year = utcDate.getFullYear();
    const month = pad(utcDate.getMonth() + 1);
    const day = pad(utcDate.getDate());
    const hours = pad(utcDate.getHours());
    const minutes = pad(utcDate.getMinutes());
    const seconds = pad(utcDate.getSeconds());

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  }

  if(preset === "isoUTC") {
    const year = utcDate.getUTCFullYear();
    const month = pad(utcDate.getUTCMonth() + 1);
    const day = pad(utcDate.getUTCDate());
    const hours = pad(utcDate.getUTCHours());
    const minutes = pad(utcDate.getUTCMinutes());
    const seconds = pad(utcDate.getUTCSeconds());
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds} UTC`;
  }

  // Pick preset if provided (excluding ISO variants which returned above)
  let presetOptions: Intl.DateTimeFormatOptions = {};
  if(preset === "short" || preset === "medium" || preset === "long") {
    presetOptions = presets[preset];
  }

  // Merge preset with custom options (custom wins)
  const formatOptions: Intl.DateTimeFormatOptions = {
    ...presetOptions,
    ...dateTimeOptions
  };

  return utcDate.toLocaleString(locale, formatOptions);


  /* Notes: 
    - All options are optional: pass only what you need.
    - Locale-aware: respects user's system settings unless you override with `locale`.
    - Time zone override: lets you display the time in any region, not just the user's local zone.
    - `preset` can be "short", "medium", or "long", "iso", or "isoUTC".
    - You can pass custom options to override the preset.
  */


  // Example usage:
  /*const utcDateString = "2026-03-07T16:43:25.489Z";

  // Default local format
  console.log(convertToLocal(utcDateString));

  // Short preset
  console.log(convertToLocal(utcDateString, { preset: "short" }));

  // Medium preset
  console.log(convertToLocal(utcDateString, { preset: "medium" }));

  // Long preset with override
  console.log(convertToLocal(utcDateString, { preset: "long", hour12: true }));

  // Force time zone with preset
  console.log(convertToLocal(utcDateString, { preset: "short", timeZone: "Africa/Lagos" }));

  // Custom format
  console.log(convertToLocal(utcDateString, {
  locale: "en-US",
  weekday: "long",
  year: "numeric",
  month: "long",
  day: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hour12: true
  }));

  // Force a specific time zone
  console.log(convertToLocal(utcDateString, {
  timeZone: "Africa/Lagos",
  hour: "2-digit",
  minute: "2-digit"
  }));

  */
}

/**
 * 
 * @param {Date} date 
 * @param {Object} options 
 * @param {boolean} [options.isUTC] - `true` if the given date is a UTC Date instance.
 * @returns 
 */
export function formatDateToYMD(date: Date, options?: { isUTC?: boolean; includeTime?: boolean }): string { 
  const { isUTC = false, includeTime = false } = options ?? {};

  if(!(date instanceof Date)) { 
    return "";
  }

  const year  = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day   = String(date.getDate()).padStart(2, "0");

  if(includeTime) {
    const hour         = String(isUTC ? date.getUTCHours() : date.getHours()).padStart(2, "0");
    const minute       = String(isUTC ? date.getUTCMinutes() : date.getMinutes()).padStart(2, "0");
    const seconds      = String(isUTC ? date.getUTCSeconds() : date.getSeconds()).padStart(2, "0");
    const milliseconds = String(isUTC ? date.getUTCMilliseconds() : date.getMilliseconds());

    return `${year}/${month}/${day} ${hour}:${minute}:${seconds}:${milliseconds}`;
  } else { 
    return `${year}-${month}-${day}`;
  }
}

/**
 * Checks if a target date is between a start and end date, inclusively.
 * @param {string|Date} dateToCheck - The date you want to verify 
 *   Local date in ISO format like: '2026-06-19T17:24:34.074'
 * @param {string|Date} startDate - UTC date in ISO format like: '2026-06-19T17:24:34.074Z'.
 * @param {string|Date} endDate - UTC date in ISO format like: '2026-06-19T17:24:34.074Z'.
 * @returns {boolean} True if the date is within the range, inclusive.
 */
export function isBetweenDates(startDate: string, endDate: string, dateToCheck?: string) { 
  dateToCheck = dateToCheck ?? toISODateString(new Date());

  // Convert all inputs into Date objects to handle both strings and Date instances
  const check = new Date(toISODateString(convertLocalDateToUTCDate(dateToCheck))); // new Date(dateToCheck);
  const start = new Date(asISODateString(startDate));
  const end = new Date(asISODateString(endDate));

  // Compare the underlying millisecond values
  return check >= start && check <= end;
}

/**
 * 
 * @param dateString - UTC date in ISO format like: '2026-06-19T17:24:34.074Z'
 * @param days - number
 */
export function isLessThanDays(dateString: string, days: number = 7) {
  try { 
    const targetDate = new Date(asISODateString(dateString));
    const now = new Date();

    const diffInMilliseconds = (now.getTime() - targetDate.getTime());
    const isWithinLimit = diffInMilliseconds < daysToMilliseconds(days);

    return isWithinLimit;
  } catch {
    return false;
  }
}

export function toISODateString(date: Date) {
  return date.toISOString();
}

// Helpers 

//  Branded Types (Nominal Typing)
// Forces you to validate the string explicitly before assigning it to the type.
type ISODate = string & { readonly __brand: unique symbol };

/**
 * Ensure a date string has the ISODate string format.
 * 
 * @param value 
 * @returns 
 */
function asISODateString(value: string): ISODate {
  if(isNaN(Date.parse(value))) {
    throw new Error(`Invalid date format ${String(value)}`);
  }

  return value as ISODate;

  // Usage:
  // const secureDate: ISODate = asISODateString("2026-06-19T17:24:34.074Z");
}

function daysToMilliseconds(days: number) {
  return days * 24 * 60 * 60 * 1000; //(Days * 24 hours * 60 minutes * 60 seconds * 1000 milliseconds)
}

